import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import axios from "axios";
import { Card, CardContent, CardMedia, Typography, Button, Grid, CircularProgress, Dialog, DialogTitle, DialogContent, TextField, DialogActions, IconButton, Tooltip, Snackbar, makeStyles } from "@material-ui/core";
import { FaRegCopy } from "react-icons/fa";
import { SERVER_NAME } from './config';
import NFTManagerABI from "./E3DNFTManager_ABI.json";
import { alpha } from '@material-ui/core/styles';

const NFTManager_ADDRESS = "0xeED4620ff525101Ffcf7327378232CA9EF778D47";
const E3DToken_ADDRESS = "0x6488861b401F427D13B6619C77C297366bCf6386";

const useStyles = makeStyles((theme) => ({
  cardMediaWrapper: {
    height: 300,
    backgroundColor: '#000000',
    overflow: 'hidden',
    position: 'relative',
    cursor: 'zoom-in'
  },
  cardMedia: {
    height: '100%',
    width: '100%',
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#000000',
    transition: 'transform 0.1s ease-out'
  },
  nftDescription: {
    color: '#cccccc',
    height: '4.5em', // Exactly 3 lines (1.5em per line)
    marginBottom: '10px',
    overflow: 'auto',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    '&::-webkit-scrollbar': {
      width: '4px'
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#4d79ff',
      borderRadius: '4px'
    }
  },
  root: {
    minHeight: '100vh',
    backgroundColor: '#000000',
    padding: '20px',
    color: '#ffffff'
  },
  header: {
    textAlign: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: '20px',
    textShadow: '0 0 15px rgba(77, 121, 255, 0.8)',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  },
  subtitle: {
    fontSize: '1.25rem',
    maxWidth: '800px',
    margin: '0 auto 20px',
    color: '#cccccc',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      marginBottom: '10px',
    },
  },
  gridContainer: {
    padding: '10px 0',
  },
  card: {
    backgroundColor: '#1e1e1e',
    color: '#ffffff',
    border: '1px solid #4d79ff',
    borderRadius: '15px',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: '0 8px 20px rgba(77, 121, 255, 0.3)',
    },
  },

  cardContent: {
    padding: '20px',
  },
  nftTitle: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    marginBottom: '10px',
  },
  nftPrice: {
    color: '#4d79ff',
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  actionButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    padding: '10px 20px',
    borderRadius: '25px',
    fontWeight: 'bold',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: alpha('#4d79ff', 0.8),
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#1e1e1e',
      color: '#ffffff',
      borderRadius: '15px',
      border: '1px solid #4d79ff',
    },
  },
  dialogTitle: {
    backgroundColor: '#2a2a2a',
  },
  dialogContent: {
    padding: '20px',
  },
  textField: {
    '& .MuiInputBase-input': {
      color: '#ffffff',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#4d79ff',
      },
      '&:hover fieldset': {
        borderColor: '#6b8fff',
      },
    },
  },
}));

const formatAddress = (address) => {
  return address ? `${address.slice(0, 6)}...${address.slice(-6)}` : "";
};

const E3DMarketplace = () => {
  const classes = useStyles();
  const [listDialogOpen, setListDialogOpen] = useState(false);
  const [listPrice, setListPrice] = useState("");
  const [listInE3D, setListInE3D] = useState(true);
  const [listError, setListError] = useState("");
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [nfts, setNfts] = useState([]);
  const [copied, setCopied] = useState(false);
  const [listing, setListing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [buyingId, setBuyingId] = useState(null);
  const [account, setAccount] = useState(null);
  const [imageScales, setImageScales] = useState({});

  useEffect(() => {
    fetchListedNFTs();
  
    const handleAccountsChanged = (accounts) => {
      if (accounts.length > 0) {
        fetchListedNFTs();
      } 
    };
  
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
    }
  
    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      }
    };
  }, []);


  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
    }).catch(err => {
      console.error("Could not copy text: ", err);
    });
  };

  const handleBuyOrManage = async (nft) => {
    if (!account) {
      alert("Connect your wallet first.");
      return;
    }

    if (nft.owner.toLowerCase() === account.toLowerCase()) {
      if (nft.isListed) {
        await unlistNFT(nft.tokenId);
      } else {
        setSelectedTokenId(nft.tokenId);
        setListDialogOpen(true);
      }
    } else {
      await buyNFT(nft);
    }
  };

  const unlistNFT = async (tokenId) => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, signer);
      const tx = await nftContract.unlistNFT(tokenId);
      await tx.wait();
      alert("NFT Unlisted Successfully!");
      fetchListedNFTs();
    } catch (error) {
      console.error("Error unlisting NFT:", error);
      alert("Unlisting failed.");
    }
  };

  const listNFT = async () => {
    try {
      setListing(true);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, signer);

      const amountInWei = ethers.utils.parseUnits(listPrice, 18);
      const tx = await nftContract.listNFT(selectedTokenId, amountInWei, listInE3D);
      await tx.wait();

      setListDialogOpen(false);
      setListError("");
      fetchListedNFTs();
    } catch (error) {
      console.error("Error listing NFT:", error);
      setListError("Failed to list NFT. Please try again.");
    } finally {
      setListing(false);
    }
  };

  const buyNFT = async (nft) => {
    try {
      setBuyingId(nft.tokenId);
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, signer);
      const e3dToken = new ethers.Contract(E3DToken_ADDRESS, [
        "function approve(address spender, uint256 amount) external returns (bool)",
        "function allowance(address owner, address spender) external view returns (uint256)"
      ], signer);

      if (nft.currency === "E3D") {
        const amountInWei = ethers.utils.parseUnits(nft.price.toString(), 18);
        const userAddress = await signer.getAddress();
        const allowance = await e3dToken.allowance(userAddress, NFTManager_ADDRESS);
  
        if (allowance.lt(amountInWei)) {
          const approveTx = await e3dToken.approve(NFTManager_ADDRESS, amountInWei);
          await approveTx.wait();
        }
      }

      const tx = await nftContract.buyNFT(nft.tokenId, nft.currency === "E3D" ? true : false);
      await tx.wait();

      //alert("NFT Purchased Successfully!");
      fetchListedNFTs();
    } catch (error) {
      console.error("Error buying NFT:", error);
      alert("Purchase failed.");
    } finally {
      setBuyingId(null);
    }
  };

  const fetchListedNFTs = async () => {
    try {
      setLoading(true);
  
      const { ethereum } = window;
  
      if (ethereum) {
        console.log("fetchListedNFTs: metamask is installed")
        const accounts = await ethereum.request({ method: 'eth_accounts' });
  
        if (accounts.length > 0) {
          console.log("fetchListedNFTs: found metamask account")

          // ✅ MetaMask is installed AND connected
          const provider = new ethers.providers.Web3Provider(ethereum);
          const signer = provider.getSigner();
          const address = await signer.getAddress();
          setAccount(address);
  
          const nftContract = new ethers.Contract(NFTManager_ADDRESS, NFTManagerABI, provider);
          const fetchedNFTs = [];
          let i = 1;
  
          while (true) {
            try {
              const uri = await nftContract.tokenURI(i);
              if (!uri) break;
  
              let ipfsUri = uri;
              if (uri.startsWith("ipfs://")) {
                ipfsUri = uri.replace("ipfs://", "https://ipfs.io/ipfs/");
              }
  
              const response = await axios.get(ipfsUri, { withCredentials: false });
              const metadata = response.data;
  
              const priceInWei = await nftContract.nftPrices(i);
              const isPriceInE3D = await nftContract.isPriceInE3D(i);
              const owner = await nftContract.ownerOf(i);
  
              const priceInE3D = ethers.utils.formatUnits(priceInWei, 18);
  
              fetchedNFTs.push({
                tokenId: i,
                price: priceInE3D,
                currency: isPriceInE3D ? 'E3D' : 'ETH',
                isListed: !priceInWei.isZero(),
                owner,
                ...metadata
              });
  
              i++;
            } catch (error) {
              break;
            }
          }
          setNfts(fetchedNFTs);
  
        } else {
          // ❌ MetaMask installed, but NOT connected
          console.log("MetaMask installed but no connected account. Falling back to server.");
          const response = await axios.get(`${SERVER_NAME}/fetchNFTs/`);
          const fetchedNFTs = response.data.nfts || [];
          setNfts(fetchedNFTs);
        }
  
      } else {
        // ❌ MetaMask not installed
        console.log("MetaMask not installed. Falling back to server.");
        const response = await axios.get(`${SERVER_NAME}/fetchNFTs/`);
        const fetchedNFTs = response.data.nfts || [];
        setNfts(fetchedNFTs);
      }
  
    } catch (error) {
      console.error("Error fetching NFTs:", error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) return (
    <div className={classes.loadingContainer}>
      <CircularProgress style={{ color: '#4d79ff' }} size={60} />
    </div>
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>

        <Typography className={classes.title} variant="h1">
          E3D NFT Marketplace
        </Typography>
        <Typography className={classes.subtitle} variant="h2">
          Discover, trade, and collect unique E3D NFTs representing blockchain visualizations
        </Typography>
      </div>
      
      <div className={classes.content}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress style={{ color: '#4d79ff' }} size={60} />
          </div>
        ) : (
          <Grid container spacing={4} className={classes.gridContainer}>
            {nfts.map((nft) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={nft.tokenId}>
                <Card className={classes.card} elevation={0}>
                <div 
                  className={classes.cardMediaWrapper}
                  onWheel={(e) => {
                    e.preventDefault();
                    const scale = imageScales[nft.tokenId] || 1;
                    const newScale = scale + (e.deltaY > 0 ? -0.1 : 0.1);
                    const clampedScale = Math.min(Math.max(newScale, 0.5), 3);
                    setImageScales(prev => ({ ...prev, [nft.tokenId]: clampedScale }));
                  }}
                >
                  <CardMedia
                    className={classes.cardMedia}
                    image={nft.image.startsWith("ipfs://") ? nft.image.replace("ipfs://", "https://ipfs.io/ipfs/") : nft.image}
                    title={`NFT ${nft.tokenId}`}
                    style={{
                      transform: `scale(${imageScales[nft.tokenId] || 1})`
                    }}
                  />
                </div>
                <CardContent className={classes.cardContent}>
                  <Typography className={classes.nftTitle} gutterBottom>
                    {nft.name || "Unnamed NFT"} (#{nft.tokenId})
                  </Typography>
                  <Typography 
                    variant="body2" 
                    className={classes.nftDescription}
                  >
                    {nft.description}
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                    <Typography variant="body2" style={{ color: '#cccccc' }}>
                      Owner: {formatAddress(nft.owner)}
                    </Typography>
                    <Tooltip title="Copy Address" placement="top">
                      <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(nft.owner)}
                        style={{ color: '#4d79ff', marginLeft: '4px' }}
                      >
                        <FaRegCopy />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <Typography className={classes.nftPrice} style={{ minHeight: '24px' }}>
                    {nft.isListed ? `${nft.price} E3D` : ''}
                  </Typography>
                  <Button
                    className={classes.actionButton}
                    fullWidth
                    onClick={() => handleBuyOrManage(nft)}
                    disabled={listing || (buyingId === nft.tokenId)}
                    style={{ marginTop: '15px' }}
                  >
                    {buyingId === nft.tokenId ? (
                      <CircularProgress size={24} style={{ color: '#ffffff' }} />
                    ) : nft.owner.toLowerCase() === account?.toLowerCase() ? (
                      nft.isListed ? "Unlist NFT" : "List for Sale"
                    ) : (
                      nft.isListed ? "Buy Now" : "Not Listed"
                    )}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      </div>
      
      <Dialog 
        open={listDialogOpen} 
        onClose={() => setListDialogOpen(false)}
        className={classes.dialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={classes.dialogTitle}>
          List NFT for Sale
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <TextField
            className={classes.textField}
            autoFocus
            margin="dense"
            label="Price"
            type="number"
            fullWidth
            value={listPrice}
            onChange={(e) => setListPrice(e.target.value)}
            error={!!listError}
            helperText={listError}
            variant="outlined"
          />
          <Button
            className={classes.actionButton}
            onClick={() => setListInE3D(!listInE3D)}
            style={{ marginTop: '20px' }}
            fullWidth
          >
            Price in: {listInE3D ? "E3D" : "ETH"}
          </Button>
        </DialogContent>
        <DialogActions style={{ padding: '20px' }}>
          <Button 
            onClick={() => setListDialogOpen(false)} 
            style={{ color: '#cccccc' }}
          >
            Cancel
          </Button>
          <Button 
            className={classes.actionButton}
            onClick={listNFT} 
            disabled={listing}
          >
            {listing ? <CircularProgress size={24} style={{ color: '#ffffff' }} /> : "List NFT"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Address copied to clipboard"
        ContentProps={{
          style: {
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
            border: '1px solid #4d79ff',
          }
        }}
      />
    </div>
  );
};

export default E3DMarketplace;
